import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const WellnessMapView = ({ centers, selectedCenter, onCenterSelect }) => {
  const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 }); // Center of US
  const [zoom, setZoom] = useState(4);
  const [clusteredMarkers, setClusteredMarkers] = useState([]);

  // Simple clustering logic for demonstration
  useEffect(() => {
    if (!centers?.length) return;

    const clusters = [];
    const processed = new Set();

    centers.forEach((center, index) => {
      if (processed.has(index)) return;

      const nearbyResults = centers.filter((other, otherIndex) => {
        if (processed.has(otherIndex) || index === otherIndex) return false;
        
        // Simple distance calculation (not accurate for real use)
        const distance = Math.sqrt(
          Math.pow(center.coordinates?.lat - other.coordinates?.lat, 2) +
          Math.pow(center.coordinates?.lng - other.coordinates?.lng, 2)
        );
        
        return distance < 1; // Cluster if within ~1 degree
      });

      if (nearbyResults.length > 0) {
        // Create cluster
        const clusterCenters = [center, ...nearbyResults];
        const avgLat = clusterCenters.reduce((sum, c) => sum + (c.coordinates?.lat || 0), 0) / clusterCenters.length;
        const avgLng = clusterCenters.reduce((sum, c) => sum + (c.coordinates?.lng || 0), 0) / clusterCenters.length;
        
        clusters.push({
          id: `cluster-${index}`,
          coordinates: { lat: avgLat, lng: avgLng },
          centers: clusterCenters,
          count: clusterCenters.length
        });

        clusterCenters.forEach((_, idx) => processed.add(centers.indexOf(clusterCenters[idx])));
      } else {
        // Single marker
        clusters.push({
          id: center.id,
          coordinates: center.coordinates,
          centers: [center],
          count: 1
        });
        processed.add(index);
      }
    });

    setClusteredMarkers(clusters);
  }, [centers]);

  const handleMarkerClick = (cluster) => {
    if (cluster.count === 1) {
      onCenterSelect?.(cluster.centers[0]);
    } else {
      // For clusters, zoom in or show list
      setMapCenter(cluster.coordinates);
      setZoom(prev => Math.min(prev + 2, 15));
    }
  };

  const getMarkerColor = (center) => {
    const typeColors = {
      'EES Center': 'from-primary to-secondary',
      'Meditation Retreat': 'from-purple-500 to-purple-600',
      'Crystal Therapy': 'from-pink-500 to-pink-600',
      'Spa Resort': 'from-green-500 to-green-600',
      'Yoga Retreat': 'from-teal-500 to-teal-600',
      'Detox Center': 'from-orange-500 to-orange-600'
    };
    return typeColors[center.type] || 'from-gray-500 to-gray-600';
  };

  const resetView = () => {
    setMapCenter({ lat: 39.8283, lng: -98.5795 });
    setZoom(4);
  };

  return (
    <div className="relative bg-white rounded-2xl shadow-elevation-2 border border-border overflow-hidden">
      {/* Map Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">
            Wellness Centers Map
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={resetView}
              className="px-3 py-1 text-sm text-secondary hover:text-primary hover:bg-blue-50 rounded-md transition-all duration-200"
            >
              Reset View
            </button>
            <div className="flex items-center space-x-1 text-sm text-text-secondary">
              <Icon name="MapPin" size={14} />
              <span>{centers?.length || 0} centers</span>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative h-96 md:h-[600px] bg-gradient-to-br from-blue-50 to-teal-50">
        {/* Mock Map Background */}
        <div className="absolute inset-0 opacity-20">
          <svg viewBox="0 0 400 300" className="w-full h-full">
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Map Controls */}
        <div className="absolute top-4 right-4 z-10 flex flex-col space-y-2">
          <button
            onClick={() => setZoom(prev => Math.min(prev + 1, 15))}
            className="w-8 h-8 bg-white border border-border rounded-md flex items-center 
                     justify-center shadow-elevation-2 hover:bg-blue-50 transition-all duration-200"
          >
            <Icon name="Plus" size={16} />
          </button>
          <button
            onClick={() => setZoom(prev => Math.max(prev - 1, 1))}
            className="w-8 h-8 bg-white border border-border rounded-md flex items-center 
                     justify-center shadow-elevation-2 hover:bg-blue-50 transition-all duration-200"
          >
            <Icon name="Minus" size={16} />
          </button>
        </div>

        {/* Markers */}
        <div className="absolute inset-0">
          {clusteredMarkers.map((cluster) => {
            const isSelected = selectedCenter && cluster.centers.some(c => c.id === selectedCenter.id);
            
            return (
              <motion.div
                key={cluster.id}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3 }}
                className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2"
                style={{
                  left: `${((cluster.coordinates?.lng + 180) / 360) * 100}%`,
                  top: `${((-cluster.coordinates?.lat + 90) / 180) * 100}%`
                }}
                onClick={() => handleMarkerClick(cluster)}
              >
                {cluster.count > 1 ? (
                  // Cluster Marker
                  <div className={`relative w-10 h-10 bg-gradient-to-r from-primary to-secondary 
                                  rounded-full flex items-center justify-center shadow-elevation-3 
                                  border-2 border-white transform transition-all duration-200 
                                  hover:scale-110 ${isSelected ? 'scale-125 ring-4 ring-blue-200' : ''}`}>
                    <span className="text-white text-sm font-bold">{cluster.count}</span>
                  </div>
                ) : (
                  // Single Marker
                  <div className={`relative w-8 h-8 bg-gradient-to-r ${getMarkerColor(cluster.centers[0])} 
                                  rounded-full flex items-center justify-center shadow-elevation-3 
                                  border-2 border-white transform transition-all duration-200 
                                  hover:scale-110 ${isSelected ? 'scale-125 ring-4 ring-blue-200' : ''}`}>
                    <Icon 
                      name={cluster.centers[0].type === 'EES Center' ? 'Zap' : 
                            cluster.centers[0].type === 'Meditation Retreat' ? 'Heart' :
                            cluster.centers[0].type === 'Crystal Therapy' ? 'Gem' :
                            cluster.centers[0].type === 'Spa Resort' ? 'Droplets' :
                            cluster.centers[0].type === 'Yoga Retreat' ? 'Users' : 'Leaf'}
                      size={12} 
                      className="text-white" 
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Selected Center Info Card */}
        {selectedCenter && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-4 left-4 right-4 bg-white rounded-xl shadow-elevation-4 
                     border border-border p-4 z-20"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="px-2 py-1 bg-secondary text-white text-xs font-medium rounded-full">
                    {selectedCenter.type}
                  </span>
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={12} className="text-yellow-400 fill-current" />
                    <span className="text-sm font-semibold">{selectedCenter.rating}</span>
                  </div>
                </div>
                <h4 className="font-semibold text-text-primary mb-1">
                  {selectedCenter.name}
                </h4>
                <div className="flex items-center text-text-secondary text-sm mb-2">
                  <Icon name="MapPin" size={12} className="mr-1" />
                  {selectedCenter.location}
                </div>
                <div className="text-text-primary">
                  <span className="font-bold">${selectedCenter.priceFrom}</span>
                  <span className="text-sm text-text-secondary"> /night</span>
                </div>
              </div>
              <button
                onClick={() => onCenterSelect?.(null)}
                className="p-1 hover:bg-gray-100 rounded-md"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Map Legend */}
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-elevation-2 p-3 z-10">
          <div className="text-sm font-semibold text-text-primary mb-2">Center Types</div>
          <div className="space-y-1">
            {[
              { type: 'EES Center', icon: 'Zap', color: 'from-primary to-secondary' },
              { type: 'Meditation', icon: 'Heart', color: 'from-purple-500 to-purple-600' },
              { type: 'Crystal Therapy', icon: 'Gem', color: 'from-pink-500 to-pink-600' },
              { type: 'Spa Resort', icon: 'Droplets', color: 'from-green-500 to-green-600' }
            ].map((item) => (
              <div key={item.type} className="flex items-center space-x-2">
                <div className={`w-4 h-4 bg-gradient-to-r ${item.color} rounded-full 
                               flex items-center justify-center`}>
                  <Icon name={item.icon} size={8} className="text-white" />
                </div>
                <span className="text-xs text-text-secondary">{item.type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessMapView;